/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/require-tagless-components */
// Ember Imports
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { service } from '@ember/service';

import { classNameBindings, classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

/**
 * file-uploader provides a contained uppy component to enable users to upload
 * files.
 *
 */
@classNames('image-uploader')
@classNameBindings(
  'constrainSize:image-uploader--constrain-size',
  'empty:image-uploader--is-empty',
  'uploading:image-uploader--uploading',
  'forceDisplay:image-uploader--force-display',
)
export default class FileUploader extends Component {
  // Services
  @service
  session;

  @service
  uppy;

  @tracked
  uppyInstance = null;

  // Props
  // allowedFileTypes optionally restricts what can be uploaded via Uppy.
  // Leave unset to accept images, see effectiveFileTypes.
  allowedFileTypes = null;

  // clickPrompt provides the word to use instead of 'browse'.
  clickPrompt = 'browse';

  // constrainSize defaults to limiting the drop target to a small size for use
  // as a form input. Setting this to false will fill the element it is
  // inserted into.
  constrainSize = true;

  // dropPrompt provides the textcopy to display in the drop target widget.
  dropPrompt = 'Drop here or %{browse}';

  // empty provides a class name binding for exposing a drop target when the
  // uploader is empty.
  empty = false;

  // forceDisplay causes the uppy instance to display over top of its child element
  forceDisplay = false;

  // mode specifies whether this is used to upload images or files.
  mode = 'image';

  // spinnerDiameter overrides the size of the upload spinner. It defaults to
  // ember-paper's own default so existing uploaders are unaffected.
  spinnerDiameter = 50;

  // uploading tells the uploader whether to show a loading spinner.
  uploading = false;

  // uploadingText provides a caption to show under the spinner while
  // uploading, e.g. 'Uploading...'. It is opt-in because many uploaders are
  // too small to fit a caption alongside the spinner.
  uploadingText = null;

  // uppyConfig provides a custom uppy config.
  uppyConfig = null;

  // onError provides a function to call each time a single file has failed to
  // upload.
  // @param file {Uppy.UppyFile}
  // @param error {Error}
  // @param response {any}
  onError() {}

  // onUpload provides a function to call for when the content has successfully
  // uploaded.
  // @param file {UppyFile}
  // @param response {any}
  onUpload() {}

  // onUploadStart provides a function to call for when the upload has started.
  onUploadStart() {}

  // onDashboardClose provides a function to call for when the uppy dashboard
  // has been closed.
  onDashboardClose() {}

  // effectiveFileTypes resolves what this uploader accepts, defaulting to the
  // image list from the uppy service. Most uploaders don't pass a list, and the
  // hardcoded default here used to be a narrower set than the attachment picker
  // allowed, so an avatar or banner silently rejected formats the same product
  // accepted elsewhere.
  //
  // Deliberately not derived from `mode`: `mode` picks the upload transport, and
  // some image uploaders use `mode="file"` to store internally rather than via
  // Cloudflare (goal category badges, for one), so it can't stand in for what
  // kind of content is wanted.
  @computed('allowedFileTypes', 'uppy.allowedImageTypes')
  get effectiveFileTypes() {
    return this.allowedFileTypes ?? this.uppy.allowedImageTypes;
  }

  // State
  // dashboardOpen contains the current open state of the uppy dashboard.
  dashboardOpen = false;

  //_toggleDashboard observes the dashboard open status to open and close the
  // uppy dashboard. Also resets Uppy on open.
  @observes('dashboardOpen')
  _toggleDashboard() {
    if (this.dashboardOpen) {
      this.uppyInstance.cancelAll();
      this.uppyInstance.getPlugin('Dashboard').openModal();
    } else {
      this.uppyInstance.getPlugin('Dashboard').closeModal();
    }
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // `classNames` is one of Ember's concatenatedProperties: it must go
    // through `set` and must not be a tracked field, or the concatenation
    // machinery fails with "parentValues is not iterable".
    this.set('classNames', [
      'image-uploader',
      this.forceDisplay ? 'hidden' : '',
    ]);
  }

  // Lifecycle
  didInsertElement() {
    super.didInsertElement(...arguments);
    this.initializeUppy();
  }

  willDestroyElement() {
    this.uppyInstance.destroy();
    super.willDestroyElement(...arguments);
  }

  // Initialize Uppy
  initializeUppy() {
    this.uppyInstance = this.uppy.initFileUploaderUppy(
      this.mode,
      this.uppyConfig,
      this.effectiveFileTypes,
      this.elementId,
      this.dropPrompt,
      this.clickPrompt,
      this.constrainSize,
      () => {
        if (this.onDashboardClose) {
          this.onDashboardClose();
        } else {
          this.set('dashboardOpen', false);
        }
      },
      this.onUploadStart,
      (file, response) => {
        this.onUpload(file, response);

        // Close Uppy Dashboard overlay (if open) when the upload has finished.
        if (this.onDashboardClose) {
          this.onDashboardClose();
        } else {
          this.set('dashboardOpen', false);
        }
      },
      this.onError,
    );
  }
}
